import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Loader2, Filter, Search, ChevronLeft, ChevronRight, User, Archive, RotateCcw, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';

export default function PatientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const api = useApi();
  
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([]);
  
  // Archive state
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  // Filters
  const [selectedClinic, setSelectedClinic] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Bulk Import State
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState('');
  const [showColumnMapper, setShowColumnMapper] = useState(false);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Create Patient State
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [newPatient, setNewPatient] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    phone: '',
    email: '',
    address: ''
  });

  // Global blood group constants
  const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadClinics();
    } else {
      setSelectedClinic(user?.clinic_id);
    }
  }, []);

  useEffect(() => {
    if (selectedClinic) {
      loadPatients();
    }
  }, [selectedClinic, showArchived]);

  useEffect(() => {
    applyFilters();
  }, [patients, searchQuery, genderFilter]);

  const loadClinics = async () => {
    try {
      const data = await api.get('/clinics/');
      const sorted = data.sort((a, b) => a.name.localeCompare(b.name));
      setClinics(sorted);
      if (sorted.length > 0) {
        setSelectedClinic(sorted[0].id);
      }
    } catch (error) {
      console.error('Failed to load clinics:', error);
      toast.error('Failed to load clinics');
    }
  };

  const loadPatients = async () => {
    setLoading(true);
    try {
      const data = await api.get(`/patients/?clinic_id=${selectedClinic}&archived_only=${showArchived}`);
      // Sort alphabetically by name
      const sorted = (data || []).sort((a, b) => 
        (a.full_name || '').localeCompare(b.full_name || '')
      );
      setPatients(sorted);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...patients];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(patient =>
        (patient.full_name && patient.full_name.toLowerCase().includes(query)) ||
        (patient.email && patient.email.toLowerCase().includes(query)) ||
        (patient.phone && patient.phone.includes(query)) ||
        (patient.patient_id && patient.patient_id.toLowerCase().includes(query))
      );
    }

    // Gender filter
    if (genderFilter !== 'all') {
      filtered = filtered.filter(patient => patient.gender === genderFilter);
    }

    setFilteredPatients(filtered);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getGenderBadge = (gender) => {
    const colors = {
      male: 'bg-blue-100 text-blue-800 border-blue-300',
      female: 'bg-pink-100 text-pink-800 border-pink-300',
      other: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return (
      <Badge className={`${colors[gender?.toLowerCase()] || 'bg-gray-100 text-gray-800'} border`}>
        {gender || 'N/A'}
      </Badge>
    );
  };

  const handleArchiveClick = (patient) => {
    setSelectedPatient(patient);
    setArchiveReason('');
    setShowArchiveDialog(true);
  };

  const handleArchivePatient = async () => {
    if (!archiveReason.trim()) {
      toast.error('Please provide a reason for archiving');
      return;
    }

    setArchiveLoading(true);
    try {
      await api.patch(`/patients/${selectedPatient.id}/archive`, {
        reason: archiveReason
      });
      toast.success('Patient archived successfully');
      setShowArchiveDialog(false);
      setSelectedPatient(null);
      setArchiveReason('');
      loadPatients();
    } catch (error) {
      console.error('Failed to archive patient:', error);
      toast.error(error.response?.data?.detail || 'Failed to archive patient');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleUnarchivePatient = async (patientId) => {
    try {
      await api.patch(`/patients/${patientId}/unarchive`);
      toast.success('Patient restored successfully');
      loadPatients();
    } catch (error) {
      console.error('Failed to unarchive patient:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore patient');
    }
  };

  // Bulk Import Functions
  const handleCsvFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    Papa.parse(file, {
      complete: (result) => {
        if (result.data && result.data.length > 0) {
          const headers = result.data[0];
          setCsvHeaders(headers);
          setCsvData(result.data.map(row => row.join(',')).join('\n'));
          
          // Auto-map common column names
          const autoMapping = {};
          const fieldMappings = {
            'Full Name': ['name', 'full name', 'patient name', 'fullname'],
            'Date of Birth': ['dob', 'date of birth', 'dateofbirth', 'birth date', 'birthdate'],
            'Gender': ['gender', 'sex'],
            'Email Address': ['email', 'email address', 'e-mail'],
            'Phone': ['phone', 'mobile', 'contact', 'phone number', 'mobile number'],
            'Address': ['address', 'location', 'residence'],
            'Medical History': ['medical history', 'history', 'medical notes', 'notes'],
            'Patient ID': ['patient id', 'id', 'patient_id', 'patientid']
          };

          headers.forEach((header, index) => {
            const normalized = header.toLowerCase().trim();
            for (const [targetField, aliases] of Object.entries(fieldMappings)) {
              if (aliases.includes(normalized) || normalized === targetField.toLowerCase()) {
                autoMapping[header] = targetField;
                break;
              }
            }
          });

          setColumnMapping(autoMapping);
          setShowColumnMapper(true);
        }
      },
      error: (error) => {
        toast.error('Failed to parse CSV file');
        console.error(error);
      }
    });
  };

  const handleImportCsv = async () => {
    // Validate required fields are mapped
    const requiredFields = ['Full Name', 'Date of Birth', 'Gender'];
    const mappedFields = Object.values(columnMapping);
    const missingFields = requiredFields.filter(f => !mappedFields.includes(f));

    if (missingFields.length > 0) {
      toast.error(`Please map required fields: ${missingFields.join(', ')}`);
      return;
    }

    setImporting(true);
    try {
      // Create mapped CSV with correct column names
      const lines = csvData.split('\n');
      const originalHeaders = csvHeaders;
      
      // Create new header row with mapped names
      const newHeaders = originalHeaders.map(h => columnMapping[h] || h);
      const mappedCsv = [newHeaders.join(','), ...lines.slice(1)].join('\n');
      
      // Create new file with mapped data
      const blob = new Blob([mappedCsv], { type: 'text/csv' });
      const file = new File([blob], csvFile.name, { type: 'text/csv' });
      
      const formData = new FormData();
      formData.append('file', file);

      const result = await api.post('/patients/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(result);
      toast.success(result.message);
      await loadPatients();
      setShowColumnMapper(false);
    } catch (error) {
      console.error("Failed to import CSV:", error);
      toast.error(error.response?.data?.detail || "Failed to import CSV");
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportResults = () => {
    setImportResults(null);
    setCsvFile(null);
    setShowImportDialog(false);
    setShowColumnMapper(false);
    setCsvHeaders([]);
    setCsvData('');
    setColumnMapping({});
  };

  // Create Patient Function
  const handleCreatePatient = async () => {
    if (!newPatient.full_name || !newPatient.date_of_birth || !newPatient.gender) {
      toast.error('Please fill in all required fields (Name, Date of Birth, Gender)');
      return;
    }

    setCreatingPatient(true);
    try {
      await api.post('/patients/', {
        ...newPatient,
        clinic_id: selectedClinic
      });
      toast.success('Patient created successfully');
      setShowCreateDialog(false);
      setNewPatient({
        full_name: '',
        date_of_birth: '',
        gender: '',
        phone: '',
        email: '',
        address: ''
      });
      loadPatients();
    } catch (error) {
      console.error('Failed to create patient:', error);
      toast.error(error.response?.data?.detail || 'Failed to create patient');
    } finally {
      setCreatingPatient(false);
    }
  };

  const downloadSampleCsv = () => {
    const sampleCsv = `Patient ID,Date of Birth,Full Name,Email Address,Phone,Gender,Address,Medical History
PAT001,1990-01-15,John Doe,john@example.com,9841234567,Male,"Ward 10, Ekantakuna, Lalitpur","Diabetics, High BP"
,1985-05-20,Jane Smith,jane@example.com,9851234567,Female,"Thamel, Kathmandu",Hypertension
PAT003,2000-12-10,Alice Johnson,,9861234567,Female,"Bhaktapur",No known conditions`;
    
    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_patients_import.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPatients = filteredPatients.slice(startIndex, endIndex);
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>All Patients</h1>
            <p className="text-gray-600 mt-1">View and manage all patient records</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-lg px-4 py-2">
              Total: {filteredPatients.length} patients
            </Badge>
            <Button
              onClick={() => setShowCreateDialog(true)}
              style={{ backgroundColor: "#652D90" }}
              className="flex items-center gap-2"
            >
              <User size={18} />
              Add Patient
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              className="flex items-center gap-2"
            >
              <Upload size={18} />
              Bulk Import
            </Button>
          </div>
        </div>

      {/* Filters */}
      <Card className="rounded-2xl border-[#e5e5e5]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: "#652D90" }}>
            <Filter size={20} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {user?.role === 'superadmin' && (
              <div className="space-y-2">
                <Label>Clinic</Label>
                <Select value={selectedClinic} onValueChange={setSelectedClinic}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {clinics.map(clinic => (
                      <SelectItem key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={genderFilter} onValueChange={setGenderFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Search name, email, phone, ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex items-center space-x-2">
            <Checkbox 
              id="show-archived" 
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
            <label
              htmlFor="show-archived"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Show archived patients
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card className="rounded-2xl border-[#e5e5e5] mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ color: "#652D90" }}>
              Patient Records ({currentPatients.length} of {filteredPatients.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-600">Rows per page:</Label>
              <Select value={itemsPerPage.toString()} onValueChange={(val) => {setItemsPerPage(Number(val)); setCurrentPage(1);}}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#652D90" }} />
            </div>
          ) : currentPatients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No patients found for the selected filters
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Patient ID</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Date of Birth</TableHead>
                      <TableHead>Age</TableHead>
                      <TableHead>Gender</TableHead>
                      <TableHead>Blood Group</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Medical History</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentPatients.map((patient) => (
                      <TableRow 
                        key={patient.id} 
                        className="hover:bg-gray-50"
                      >
                        <TableCell className="font-mono text-sm">
                          <Badge variant="outline">{patient.patient_id || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell className="font-medium cursor-pointer" style={{ color: "#652D90" }} onClick={() => navigate(`/patient/${patient.id}`)}>
                          <div className="flex items-center gap-2">
                            <User size={16} />
                            {patient.full_name}
                            {patient.is_archived && (
                              <Badge className="bg-orange-100 text-orange-700 border-orange-300">
                                Archived
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(patient.date_of_birth)}</TableCell>
                        <TableCell className="text-sm">{calculateAge(patient.date_of_birth) || 'N/A'}</TableCell>
                        <TableCell>{getGenderBadge(patient.gender)}</TableCell>
                        <TableCell className="text-sm">{patient.blood_group || 'N/A'}</TableCell>
                        <TableCell className="text-sm font-mono">{patient.phone || 'N/A'}</TableCell>
                        <TableCell className="text-sm">{patient.email || 'N/A'}</TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-xs truncate" title={patient.medical_history}>
                          {patient.medical_history || 'None'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {patient.is_archived ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnarchivePatient(patient.id)}
                              className="text-green-600 border-green-300 hover:bg-green-50"
                            >
                              <RotateCcw size={16} className="mr-1" />
                              Restore
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleArchiveClick(patient)}
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                            >
                              <Archive size={16} className="mr-1" />
                              Archive
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredPatients.length)} of {filteredPatients.length} results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {currentPage} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Archive Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Patient</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              You are about to archive <strong>{selectedPatient?.full_name}</strong>. 
              This will hide the patient from the active patient list. You can restore them later.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive-reason">Reason for archiving *</Label>
              <Textarea
                id="archive-reason"
                placeholder="e.g., Patient moved to another city, Treatment completed, etc."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowArchiveDialog(false)}
              disabled={archiveLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchivePatient}
              disabled={archiveLoading || !archiveReason.trim()}
              style={{ backgroundColor: "#652D90" }}
            >
              {archiveLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archive Patient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Import Patients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <p className="text-sm text-blue-900 font-medium">Import Instructions:</p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                <li>Upload a CSV file with patient data</li>
                <li><strong>Required fields:</strong> Full Name, Date of Birth, Gender</li>
                <li>Optional fields: Patient ID, Email, Phone, Address, Medical History</li>
                <li>Date format: YYYY-MM-DD (e.g., 1990-01-15)</li>
              </ul>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadSampleCsv}
                className="mt-2 w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Sample CSV Template
              </Button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvFileSelect}
                className="hidden"
                id="csv-upload-input"
              />
              <label htmlFor="csv-upload-input">
                <Button variant="outline" onClick={() => document.getElementById('csv-upload-input').click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Select CSV File
                </Button>
              </label>
              {csvFile && (
                <p className="mt-3 text-sm text-gray-600">
                  Selected: <strong>{csvFile.name}</strong>
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Mapping Dialog */}
      <Dialog open={showColumnMapper} onOpenChange={setShowColumnMapper}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map CSV Columns to Patient Fields</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Match your CSV columns to the patient fields below. <span className="text-red-600 font-medium">Required fields are marked with *</span>
            </p>
            <div className="space-y-3">
              {csvHeaders.map((header, index) => (
                <div key={index} className="grid grid-cols-2 gap-4 items-center">
                  <div className="font-medium text-sm">{header}</div>
                  <Select
                    value={columnMapping[header] || ''}
                    onValueChange={(value) => setColumnMapping({...columnMapping, [header]: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full Name">Full Name *</SelectItem>
                      <SelectItem value="Date of Birth">Date of Birth *</SelectItem>
                      <SelectItem value="Gender">Gender *</SelectItem>
                      <SelectItem value="Patient ID">Patient ID</SelectItem>
                      <SelectItem value="Email Address">Email Address</SelectItem>
                      <SelectItem value="Phone">Phone</SelectItem>
                      <SelectItem value="Address">Address</SelectItem>
                      <SelectItem value="Medical History">Medical History</SelectItem>
                      <SelectItem value="ignore">-- Ignore this column --</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-900">
                <strong>Note:</strong> Ensure the required fields (Full Name, Date of Birth, Gender) are mapped correctly.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowColumnMapper(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImportCsv}
              disabled={importing}
              style={{ backgroundColor: "#652D90" }}
            >
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import Patients
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Results Dialog */}
      {importResults && (
        <Dialog open={!!importResults} onOpenChange={handleCloseImportResults}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Results</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-green-50">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">{importResults.added_count}</p>
                      <p className="text-sm text-green-600">Patients Added</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-700">{importResults.discarded_count}</p>
                      <p className="text-sm text-red-600">Rows Discarded</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {importResults.added_patients && importResults.added_patients.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 mb-2">Successfully Added:</h4>
                  <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1">
                    {importResults.added_patients.map((p, idx) => (
                      <div key={idx} className="text-sm bg-white p-2 rounded">
                        <strong>{p.full_name}</strong> - {p.patient_id}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importResults.discarded_patients && importResults.discarded_patients.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2">Discarded Rows:</h4>
                  <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1">
                    {importResults.discarded_patients.map((p, idx) => (
                      <div key={idx} className="text-sm bg-white p-2 rounded">
                        <strong>Row {p.row}:</strong> {p.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={handleCloseImportResults} style={{ backgroundColor: "#652D90" }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Patient Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Patient</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={newPatient.full_name}
                onChange={(e) => setNewPatient({ ...newPatient, full_name: e.target.value })}
                placeholder="Enter patient name"
              />
            </div>
            <div>
              <Label>Date of Birth *</Label>
              <Input
                type="date"
                value={newPatient.date_of_birth}
                onChange={(e) => setNewPatient({ ...newPatient, date_of_birth: e.target.value })}
              />
            </div>
            <div>
              <Label>Gender *</Label>
              <Select value={newPatient.gender} onValueChange={(value) => setNewPatient({ ...newPatient, gender: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Blood Group</Label>
              <Select value={newPatient.blood_group} onValueChange={(value) => setNewPatient({ ...newPatient, blood_group: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select blood group" />
                </SelectTrigger>
                <SelectContent>
                  {BLOOD_GROUPS.map(bg => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newPatient.phone}
                onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newPatient.email}
                onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={newPatient.address}
                onChange={(e) => setNewPatient({ ...newPatient, address: e.target.value })}
                placeholder="Enter address"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePatient} 
              disabled={creatingPatient}
              style={{ backgroundColor: "#652D90" }}
            >
              {creatingPatient ? 'Creating...' : 'Create Patient'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
